import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { ADMIN_API_URL, apiFetch, getAuthToken } from "../utils/apiConfig";
import styles from "./EmailMarketing.module.css";

const STEPS = [
    { id: 1, label: "Audience", icon: "👥" },
    { id: 2, label: "Design", icon: "✨" },
    { id: 3, label: "Edit & Preview", icon: "✏️" },
    { id: 4, label: "Send", icon: "🚀" },
];

const PHOTO_CATEGORIES = [
    { id: "all", label: "All", icon: "🌐" },
    { id: "travel", label: "Travel", icon: "✈️" },
    { id: "beach", label: "Beach", icon: "🏖️" },
    { id: "city", label: "City", icon: "🏙️" },
    { id: "mountain", label: "Mountain", icon: "🏔️" },
    { id: "hotel", label: "Hotel", icon: "🏨" },
    { id: "food", label: "Food", icon: "🍽️" },
    { id: "adventure", label: "Adventure", icon: "🎯" },
    { id: "sunset", label: "Sunset", icon: "🌅" },
    { id: "airplane", label: "Airplane", icon: "🛩️" },
    { id: "nature", label: "Nature", icon: "🌿" },
    { id: "culture", label: "Culture", icon: "🏛️" },
    { id: "wellness", label: "Wellness", icon: "🧘" },
];

const FONT_FAMILIES = [
    { value: "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", label: "System Default" },
    { value: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", label: "Segoe UI" },
    { value: "Georgia, 'Times New Roman', Times, serif", label: "Georgia (Serif)" },
    { value: "'Courier New', Courier, monospace", label: "Courier (Mono)" },
    { value: "Arial, Helvetica, sans-serif", label: "Arial" },
    { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
    { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
    { value: "'Trebuchet MS', Helvetica, sans-serif", label: "Trebuchet" },
];

const FONT_SIZES = ["10px", "12px", "13px", "14px", "15px", "16px", "18px", "20px", "22px", "24px", "28px", "32px", "36px", "40px", "48px"];

async function uploadFile(file) {
    const formData = new FormData();
    formData.append("image", file);
    const token = getAuthToken();
    const resp = await fetch(`${ADMIN_API_URL}/email/upload-image`, {
        method: "POST", credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
    });
    return resp.json();
}

// ============================================
// PARSE EMAIL HTML INTO EDITABLE ELEMENTS
// ============================================
function parseEmailElements(html) {
    const elements = [];
    // Headlines
    const hRegex = /<(h[1-6])\s*([^>]*)>([\s\S]*?)<\/\1>/gi;
    let m;
    while ((m = hRegex.exec(html)) !== null) {
        elements.push({ type: "heading", tag: m[1], attrs: m[2], content: m[3].replace(/<[^>]*>/g, ""), fullMatch: m[0], index: m.index, rawContent: m[3] });
    }
    // Paragraphs
    const pRegex = /<p\s*([^>]*)>([\s\S]*?)<\/p>/gi;
    while ((m = pRegex.exec(html)) !== null) {
        const txt = m[2].replace(/<[^>]*>/g, "").trim();
        if (txt.length > 3) elements.push({ type: "paragraph", tag: "p", attrs: m[1], content: txt, fullMatch: m[0], index: m.index, rawContent: m[2] });
    }
    // Links
    const aRegex = /<a\s+([^>]*href=["'][^"']*["'][^>]*)>([\s\S]*?)<\/a>/gi;
    while ((m = aRegex.exec(html)) !== null) {
        const hrefMatch = m[1].match(/href=["']([^"']*)["']/);
        const txt = m[2].replace(/<[^>]*>/g, "").trim();
        if (txt.length > 0) elements.push({ type: "link", tag: "a", attrs: m[1], content: txt, href: hrefMatch ? hrefMatch[1] : "", fullMatch: m[0], index: m.index, rawContent: m[2] });
    }
    // Images
    const imgRegex = /<img\s+([^>]*)>/gi;
    while ((m = imgRegex.exec(html)) !== null) {
        const srcMatch = m[1].match(/src=["']([^"']*)["']/);
        const altMatch = m[1].match(/alt=["']([^"']*)["']/);
        elements.push({ type: "image", tag: "img", attrs: m[1], src: srcMatch ? srcMatch[1] : "", alt: altMatch ? altMatch[1] : "", fullMatch: m[0], index: m.index });
    }
    // Sort by position
    elements.sort((a, b) => a.index - b.index);
    return elements;
}

function getStyleValue(attrs, prop) {
    const styleMatch = attrs.match(/style=["']([^"']*)["']/i);
    if (!styleMatch) return "";
    const styles = styleMatch[1];
    const propRegex = new RegExp(`${prop}\\s*:\\s*([^;]+)`, "i");
    const match = styles.match(propRegex);
    return match ? match[1].trim() : "";
}

function setStyleValue(attrs, prop, value) {
    const styleMatch = attrs.match(/style=["']([^"']*)["']/i);
    if (!styleMatch) {
        return attrs + ` style="${prop}: ${value};"`;
    }
    let styleStr = styleMatch[1];
    const propRegex = new RegExp(`${prop}\\s*:[^;]+;?`, "i");
    if (propRegex.test(styleStr)) {
        styleStr = styleStr.replace(propRegex, `${prop}: ${value};`);
    } else {
        styleStr = styleStr.replace(/;?\s*$/, "; " + `${prop}: ${value};`);
    }
    return attrs.replace(/style=["'][^"']*["']/i, `style="${styleStr}"`);
}

// ============================================
// MAIN COMPONENT
// ============================================
export const EmailMarketing = () => {
    const [step, setStep] = useState(1);

    // Audience
    const [customers, setCustomers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [filters, setFilters] = useState({ search: "", role: "all", active: "all", joinedAfter: "", joinedBefore: "" });

    // Design
    const [aiPrompt, setAiPrompt] = useState("");
    const [generating, setGenerating] = useState(false);
    const [generationError, setGenerationError] = useState("");
    const [logoUrl, setLogoUrl] = useState("");
    const [logoPreview, setLogoPreview] = useState("");
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [imageUrls, setImageUrls] = useState([]);

    // Edit
    const [subject, setSubject] = useState("");
    const [emailHtml, setEmailHtml] = useState("");
    const [editMode, setEditMode] = useState("preview"); // 'preview' | 'visual' | 'code'
    const previewRef = useRef(null);

    // Image Panel
    const [showImagePanel, setShowImagePanel] = useState(false);
    const [imagePanelTab, setImagePanelTab] = useState("current");
    const [photoCategory, setPhotoCategory] = useState("all");
    const [curatedPhotos, setCuratedPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState("");
    const [swappingImageIndex, setSwappingImageIndex] = useState(null);

    // Visual Editor
    const [selectedElement, setSelectedElement] = useState(null); // index into parsedElements
    const [elementEditText, setElementEditText] = useState("");
    const [elementEditHref, setElementEditHref] = useState("");

    // Find & Replace
    const [showFindReplace, setShowFindReplace] = useState(false);
    const [findText, setFindText] = useState("");
    const [replaceText, setReplaceText] = useState("");

    // Templates
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [templateDesc, setTemplateDesc] = useState("");
    const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);

    // Send
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [testSending, setTestSending] = useState(false);
    const [scheduledAt, setScheduledAt] = useState("");

    // AI Chat Editor
    const [showAiChat, setShowAiChat] = useState(false);
    const [aiChatInput, setAiChatInput] = useState("");
    const [aiChatMessages, setAiChatMessages] = useState([]);
    const [aiEditing, setAiEditing] = useState(false);
    const aiChatEndRef = useRef(null);

    // History
    const [campaigns, setCampaigns] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [expandedCampaign, setExpandedCampaign] = useState(null);
    const [campaignRecipients, setCampaignRecipients] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [retrying, setRetrying] = useState(false);

    // ===================== PARSED ELEMENTS =====================
    const parsedElements = useMemo(() => parseEmailElements(emailHtml), [emailHtml]);

    const emailImages = useMemo(() => {
        const regex = /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi;
        const imgs = [];
        let match;
        while ((match = regex.exec(emailHtml)) !== null) {
            const altMatch = match[0].match(/alt=["']([^"']*)["']/i);
            imgs.push({ fullTag: match[0], src: match[1], alt: altMatch ? altMatch[1] : "", startIndex: match.index });
        }
        return imgs;
    }, [emailHtml]);

    // ===================== DATA FETCHING =====================
    const fetchCustomers = useCallback(async () => {
        try {
            setLoadingCustomers(true);
            const params = new URLSearchParams();
            if (filters.role !== "all") params.set("role", filters.role);
            if (filters.active !== "all") params.set("active", filters.active);
            if (filters.search) params.set("search", filters.search);
            if (filters.joinedAfter) params.set("joinedAfter", filters.joinedAfter);
            if (filters.joinedBefore) params.set("joinedBefore", filters.joinedBefore);
            const resp = await apiFetch(`${ADMIN_API_URL}/email/customers?${params}`);
            const data = await resp.json();
            setCustomers(data.customers || []);
        } catch (err) { console.error(err); }
        finally { setLoadingCustomers(false); }
    }, [filters]);

    const fetchTemplates = useCallback(async () => {
        try {
            const resp = await apiFetch(`${ADMIN_API_URL}/email/templates`);
            const data = await resp.json();
            setTemplates(data.templates || []);
        } catch { setTemplates([]); }
    }, []);

    const fetchPhotos = useCallback(async (cat) => {
        setPhotosLoading(true);
        try {
            const params = new URLSearchParams();
            if (cat && cat !== "all") params.set("category", cat);
            const resp = await apiFetch(`${ADMIN_API_URL}/email/photos?${params}`);
            const data = await resp.json();
            setCuratedPhotos(data.photos || []);
        } catch { setCuratedPhotos([]); }
        finally { setPhotosLoading(false); }
    }, []);

    const fetchCampaigns = useCallback(async () => {
        try {
            const resp = await apiFetch(`${ADMIN_API_URL}/email/campaigns`);
            const data = await resp.json();
            setCampaigns(data.campaigns || []);
        } catch { setCampaigns([]); }
    }, []);

    const fetchCampaignDetails = useCallback(async (id) => {
        setLoadingRecipients(true);
        try {
            const resp = await apiFetch(`${ADMIN_API_URL}/email/campaigns/${id}`);
            const data = await resp.json();
            setCampaignRecipients(data.recipients || []);
        } catch { setCampaignRecipients([]); }
        finally { setLoadingRecipients(false); }
    }, []);

    const retryCampaign = useCallback(async (campaignId, recipientIds) => {
        setRetrying(true);
        try {
            const resp = await apiFetch(`${ADMIN_API_URL}/email/retry/${campaignId}`, {
                method: "POST",
                body: JSON.stringify({ recipientIds }),
            });
            const data = await resp.json();
            if (data.success) {
                alert(`✅ Retried ${data.retried}: ${data.succeeded} succeeded, ${data.stillFailed} still failed`);
                fetchCampaignDetails(campaignId);
                fetchCampaigns();
            } else {
                alert(`❌ ${data.error}`);
            }
        } catch { alert("Retry failed"); }
        finally { setRetrying(false); }
    }, [fetchCampaignDetails, fetchCampaigns]);

    const cancelScheduledCampaign = useCallback(async (campaignId) => {
        if (!window.confirm("Cancel this scheduled campaign?")) return;
        try {
            await apiFetch(`${ADMIN_API_URL}/email/campaigns/${campaignId}/cancel`, { method: "POST" });
            fetchCampaigns();
        } catch { alert("Failed to cancel"); }
    }, [fetchCampaigns]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
    useEffect(() => {
        if (showImagePanel && (imagePanelTab === "photos" || (step === 2 && showImagePanel))) fetchPhotos(photoCategory);
    }, [showImagePanel, imagePanelTab, photoCategory, fetchPhotos, step]);

    // ===================== SELECTION =====================
    const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const selectAll = () => setSelectedIds(prev => prev.length === customers.length ? [] : customers.map(c => c.id));

    // ===================== UPLOADS =====================
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoPreview(URL.createObjectURL(file));
        setUploadingLogo(true);
        try { const d = await uploadFile(file); setLogoUrl(d.success ? d.url : URL.createObjectURL(file)); }
        catch { setLogoUrl(URL.createObjectURL(file)); }
        finally { setUploadingLogo(false); }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const local = URL.createObjectURL(file);
        setUploadingImage(true);
        try {
            const d = await uploadFile(file);
            const url = d.success ? d.url : local;
            setUploadedImages(prev => [{ url, name: file.name, thumb: local }, ...prev]);
            if (swappingImageIndex !== null) { replaceImageInHtml(swappingImageIndex, url); setSwappingImageIndex(null); }
        } catch { setUploadedImages(prev => [{ url: local, name: file.name, thumb: local }, ...prev]); }
        finally { setUploadingImage(false); }
    };

    // ===================== IMAGE OPS =====================
    const replaceImageInHtml = (idx, newUrl) => {
        const img = emailImages[idx];
        if (!img) return;
        setEmailHtml(prev => prev.replace(img.fullTag, img.fullTag.replace(img.src, newUrl)));
        setSwappingImageIndex(null);
    };

    const removeImageFromHtml = (idx) => {
        const img = emailImages[idx];
        if (!img) return;
        setEmailHtml(prev => prev.replace(img.fullTag, ""));
    };

    const insertImageUrl = (url) => {
        const tag = `<img src="${url}" alt="Email image" width="100%" style="display:block;border-radius:8px;margin:16px 0;" />`;
        const pos = emailHtml.lastIndexOf("</table>");
        if (pos > 0) setEmailHtml(prev => prev.slice(0, pos) + tag + "\n" + prev.slice(pos));
        else setEmailHtml(prev => prev + "\n" + tag);
    };

    const copyImageUrl = (url) => { navigator.clipboard.writeText(url).then(() => { setCopiedUrl(url); setTimeout(() => setCopiedUrl(""), 2000); }); };
    const addToEmailImages = (url) => setImageUrls(prev => prev.includes(url) ? prev : [...prev, url]);
    const removeFromEmailImages = (url) => setImageUrls(prev => prev.filter(u => u !== url));

    // ===================== VISUAL EDITOR — ELEMENT OPS =====================
    const selectElementForEdit = (idx) => {
        const el = parsedElements[idx];
        if (!el) return;
        setSelectedElement(idx);
        setElementEditText(el.content || el.alt || "");
        setElementEditHref(el.href || "");
    };

    const updateElementText = (newText) => {
        const el = parsedElements[selectedElement];
        if (!el) return;
        setElementEditText(newText);
        let newMatch;
        if (el.type === "image") {
            newMatch = el.fullMatch.replace(/alt=["'][^"']*["']/i, `alt="${newText}"`);
        } else {
            // Replace the inner text content while preserving child HTML tags
            newMatch = el.fullMatch.replace(el.rawContent || el.content, newText);
        }
        setEmailHtml(prev => prev.replace(el.fullMatch, newMatch));
    };

    const updateElementHref = (newHref) => {
        const el = parsedElements[selectedElement];
        if (!el || el.type !== "link") return;
        setElementEditHref(newHref);
        const newMatch = el.fullMatch.replace(/href=["'][^"']*["']/, `href="${newHref}"`);
        setEmailHtml(prev => prev.replace(el.fullMatch, newMatch));
    };

    const updateElementStyle = (prop, value) => {
        const el = parsedElements[selectedElement];
        if (!el) return;
        const newAttrs = setStyleValue(el.attrs, prop, value);
        const newMatch = el.fullMatch.replace(el.attrs, newAttrs);
        setEmailHtml(prev => prev.replace(el.fullMatch, newMatch));
    };

    const deleteElement = () => {
        const el = parsedElements[selectedElement];
        if (!el) return;
        setEmailHtml(prev => prev.replace(el.fullMatch, ""));
        setSelectedElement(null);
    };

    // ===================== TEMPLATES =====================
    const saveTemplate = async () => {
        if (!templateName.trim() || !emailHtml) return;
        setSavingTemplate(true);
        try {
            const body = { name: templateName, description: templateDesc, subject, html_content: emailHtml, category: "custom" };
            if (editingTemplateId) {
                await apiFetch(`${ADMIN_API_URL}/email/templates/${editingTemplateId}`, { method: "PUT", body: JSON.stringify(body) });
            } else {
                await apiFetch(`${ADMIN_API_URL}/email/templates`, { method: "POST", body: JSON.stringify(body) });
            }
            fetchTemplates();
            setShowSaveTemplateModal(false);
            setTemplateName("");
            setTemplateDesc("");
            setEditingTemplateId(null);
        } catch { alert("Failed to save template"); }
        finally { setSavingTemplate(false); }
    };

    const loadTemplate = async (id) => {
        try {
            const resp = await apiFetch(`${ADMIN_API_URL}/email/templates/${id}`);
            const data = await resp.json();
            if (data.template) {
                setEmailHtml(data.template.html_content);
                if (data.template.subject) setSubject(data.template.subject);
                setShowTemplates(false);
                setStep(3);
            }
        } catch { alert("Failed to load template"); }
    };

    const deleteTemplate = async (id) => {
        if (!window.confirm("Delete this template?")) return;
        try {
            await apiFetch(`${ADMIN_API_URL}/email/templates/${id}`, { method: "DELETE" });
            fetchTemplates();
        } catch { alert("Failed to delete"); }
    };

    // ===================== FIND & REPLACE =====================
    const doFindReplace = () => { if (findText) setEmailHtml(prev => prev.split(findText).join(replaceText)); };

    // ===================== AI CHAT EDIT =====================
    const sendAiEdit = async () => {
        const instruction = aiChatInput.trim();
        if (!instruction || !emailHtml || aiEditing) return;
        setAiChatInput("");
        setAiChatMessages(prev => [...prev, { role: "user", text: instruction }]);
        setAiEditing(true);
        try {
            const resp = await apiFetch(`${ADMIN_API_URL}/email/ai-edit`, {
                method: "POST",
                body: JSON.stringify({ html: emailHtml, instruction }),
            });
            const data = await resp.json();
            if (data.success && data.html) {
                setEmailHtml(data.html);
                setAiChatMessages(prev => [...prev, { role: "assistant", text: "✅ Done! I've updated the email based on your request." }]);
            } else {
                setAiChatMessages(prev => [...prev, { role: "assistant", text: `❌ ${data.error || "Failed to apply changes. Try rephrasing your request."}` }]);
            }
        } catch (err) {
            console.error(err);
            setAiChatMessages(prev => [...prev, { role: "assistant", text: "❌ Network error — couldn't reach the AI." }]);
        } finally {
            setAiEditing(false);
        }
    };

    // Auto-scroll AI chat
    useEffect(() => {
        if (aiChatEndRef.current) aiChatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, [aiChatMessages]);

    // ===================== GENERATE =====================
    const generateEmail = async () => {
        if (!aiPrompt.trim()) return;
        try {
            setGenerating(true); setGenerationError("");
            const resp = await apiFetch(`${ADMIN_API_URL}/email/generate`, {
                method: "POST",
                body: JSON.stringify({ prompt: aiPrompt, recipientCount: selectedIds.length, logoUrl: logoUrl || undefined, imageUrls: imageUrls.length > 0 ? imageUrls : undefined }),
            });
            const data = await resp.json();
            if (data.success) {
                setEmailHtml(data.html);
                if (!subject) { const m = data.html.match(/<title>(.*?)<\/title>/i); setSubject(m ? m[1] : "A message from RoamRite"); }
                setStep(3);
            } else setGenerationError(data.error || "Generation failed");
        } catch (err) { setGenerationError("Failed to connect to AI"); console.error(err); }
        finally { setGenerating(false); }
    };

    // ===================== SEND =====================
    const sendTestEmail = async () => {
        try {
            setTestSending(true);
            const resp = await apiFetch(`${ADMIN_API_URL}/email/test`, { method: "POST", body: JSON.stringify({ subject, html: emailHtml }) });
            const data = await resp.json();
            alert(data.success ? `✅ Test sent to ${data.sentTo}` : `❌ ${data.error}`);
        } catch { alert("Failed"); }
        finally { setTestSending(false); }
    };

    const sendCampaign = async () => {
        if (!selectedIds.length || !subject || !emailHtml) return;
        const isScheduled = scheduledAt && new Date(scheduledAt) > new Date();
        const msg = isScheduled
            ? `Schedule email to ${selectedIds.length} customer${selectedIds.length !== 1 ? "s" : ""} for ${new Date(scheduledAt).toLocaleString()}?`
            : `Send to ${selectedIds.length} customer${selectedIds.length !== 1 ? "s" : ""} now?`;
        if (!window.confirm(msg)) return;
        try {
            setSending(true); setSendResult(null);
            const resp = await apiFetch(`${ADMIN_API_URL}/email/send`, {
                method: "POST",
                body: JSON.stringify({
                    customerIds: selectedIds, subject, html: emailHtml,
                    ...(isScheduled ? { scheduledAt } : {}),
                }),
            });
            const data = await resp.json();
            setSendResult(data);
            fetchCampaigns();
        } catch { setSendResult({ success: false, error: "Network error" }); }
        finally { setSending(false); }
    };

    // ===================== QUICK PROMPTS =====================
    const quickPrompts = [
        { label: "🎉 Welcome", prompt: "Warm welcome email for new users. Highlight smart expense tracking, AI activity suggestions, and trip planning. Personal tone with 'Start Your First Trip' CTA." },
        { label: "🆕 Feature", prompt: "Announce Trip Toolkit: packing list, currency converter, travel phrases, documents, emergency info. Modern tech-forward design." },
        { label: "🌍 Inspire", prompt: "Travel inspiration with trending destinations. Stunning hero imagery, top 3 destination cards. 'Plan Your Trip' CTA." },
        { label: "💤 Re-engage", prompt: "Win back inactive users. New features, AI suggestions, better budgeting. 'Welcome Back' CTA." },
        { label: "📊 Recap", prompt: "Monthly recap: trips planned, countries explored, features shipped. Infographic style. 'Next Adventure' CTA." },
        { label: "🎄 Holiday", prompt: "Seasonal holiday email. Holiday trip planning, travel tools. Warm, elegant, minimal design." },
    ];

    // ===================== PHOTO GRID =====================
    const PhotoGrid = ({ onSelect, selectedUrls = [] }) => (
        <div className={styles.panelContent}>
            <div className={styles.photoCategoryBar}>
                {PHOTO_CATEGORIES.map(cat => (
                    <button key={cat.id} className={`${styles.photoCategoryBtn} ${photoCategory === cat.id ? styles.photoCategoryBtnActive : ""}`}
                        onClick={() => setPhotoCategory(cat.id)}>
                        <span>{cat.icon}</span><span>{cat.label}</span>
                    </button>
                ))}
            </div>
            {photosLoading ? <div className={styles.loadingState}><div className={styles.spinner} /></div> : (
                <div className={styles.panelGrid}>
                    {curatedPhotos.map(p => (
                        <div key={p.id} className={`${styles.panelPhoto} ${selectedUrls.includes(p.url) ? styles.panelPhotoSelected : ""}`} onClick={() => onSelect(p)}>
                            <img src={p.thumb} alt={p.alt} loading="lazy" />
                            <div className={styles.panelPhotoActions}>
                                <span className="material-symbols-outlined">{selectedUrls.includes(p.url) ? "check_circle" : swappingImageIndex !== null ? "swap_horiz" : "add_photo_alternate"}</span>
                            </div>
                            <span className={styles.panelPhotoCredit}>{p.author}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ===================== SELECTED ELEMENT DATA =====================
    const selEl = selectedElement !== null ? parsedElements[selectedElement] : null;
    const selFontFamily = selEl ? getStyleValue(selEl.attrs, "font-family") : "";
    const selFontSize = selEl ? getStyleValue(selEl.attrs, "font-size") : "";
    const selColor = selEl ? getStyleValue(selEl.attrs, "color") : "";
    const selBgColor = selEl ? getStyleValue(selEl.attrs, "background-color") || getStyleValue(selEl.attrs, "background") : "";
    const selAlign = selEl ? getStyleValue(selEl.attrs, "text-align") : "";
    const selFontWeight = selEl ? getStyleValue(selEl.attrs, "font-weight") : "";

    // ===================== RENDER =====================
    const personalizeHtml = (h) => h.replace(/\{\{firstName\}\}/g, "John").replace(/\{\{lastName\}\}/g, "Doe").replace(/\{\{fullName\}\}/g, "John Doe").replace(/\{\{email\}\}/g, "john@example.com").replace(/\{\{unsubscribeUrl\}\}/g, "#");

    return (
        <div className={styles.container}>
            {/* HEADER */}
            <header className={styles.header}>
                <div className={styles.headerTop}>
                    <Link to="/admin" className={styles.backBtn}><span className="material-symbols-outlined">arrow_back</span> Admin</Link>
                    <div className={styles.headerTitle}>
                        <span className={styles.headerIcon}>📧</span>
                        <div><h1>Email Campaign Studio</h1><p>AI-powered email designer</p></div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className={styles.historyBtn} onClick={() => { setShowTemplates(!showTemplates); setShowHistory(false); }}>
                            <span className="material-symbols-outlined">description</span><span>Templates</span>
                        </button>
                        <button className={styles.historyBtn} onClick={() => { setShowHistory(!showHistory); setShowTemplates(false); }}>
                            <span className="material-symbols-outlined">history</span><span>History</span>
                        </button>
                    </div>
                </div>
                <div className={styles.stepper}>
                    {STEPS.map(s => (
                        <button key={s.id} className={`${styles.stepItem} ${step === s.id ? styles.stepActive : ""} ${step > s.id ? styles.stepDone : ""}`}
                            onClick={() => s.id < step && setStep(s.id)} disabled={s.id > step}>
                            <span className={styles.stepIcon}>{step > s.id ? "✓" : s.icon}</span>
                            <span className={styles.stepLabel}>{s.label}</span>
                        </button>
                    ))}
                </div>
            </header>

            {/* TEMPLATES PANEL */}
            {showTemplates && (
                <div className={styles.historyPanel}>
                    <div className={styles.historyHeader}>
                        <h3>📋 My Templates</h3>
                        <button onClick={() => setShowTemplates(false)} className={styles.closeBtn}>×</button>
                    </div>
                    {templates.length === 0 ? <p className={styles.emptyState}>No templates saved yet. Generate an email and save it as a template!</p> : (
                        <div className={styles.templateList}>
                            {templates.map(t => (
                                <div key={t.id} className={styles.templateItem}>
                                    <div className={styles.templateItemInfo}>
                                        <strong>{t.name}</strong>
                                        {t.description && <span className={styles.templateItemDesc}>{t.description}</span>}
                                        <span className={styles.templateItemMeta}>
                                            {t.subject && <><span className="material-symbols-outlined" style={{ fontSize: 12 }}>mail</span> {t.subject} · </>}
                                            {new Date(t.updated_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <div className={styles.templateItemActions}>
                                        <button className={styles.templateLoadBtn} onClick={() => loadTemplate(t.id)}>
                                            <span className="material-symbols-outlined">open_in_new</span> Use
                                        </button>
                                        <button className={styles.templateDeleteBtn} onClick={() => deleteTemplate(t.id)}>
                                            <span className="material-symbols-outlined">delete</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* HISTORY */}
            {showHistory && (
                <div className={styles.historyPanel}>
                    <div className={styles.historyHeader}><h3>📋 Campaign History</h3><button onClick={() => setShowHistory(false)} className={styles.closeBtn}>×</button></div>
                    {campaigns.length === 0 ? <p className={styles.emptyState}>No campaigns yet</p> : (
                        <div className={styles.historyList}>
                            {campaigns.map((c) => (
                                <div key={c.id} className={styles.historyItem}>
                                    <div className={styles.historyItemHeader}>
                                        <strong>{c.subject}</strong>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span className={`${styles.campaignStatusBadge} ${styles[`campaignStatus_${c.status}`] || ""}`}>{c.status === "scheduled" ? "⏰ Scheduled" : c.status === "sending" ? "📤 Sending" : c.status === "cancelled" ? "🚫 Cancelled" : "✅ Sent"}</span>
                                            <span className={styles.historyDate}>{c.status === "scheduled" && c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : new Date(c.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                    <div className={styles.historyStats}>
                                        <span className={styles.historyStatSuccess}>✅ {c.successful}</span>
                                        {c.failed > 0 && <span className={styles.historyStatFail}>❌ {c.failed}</span>}
                                        <span>📨 {c.total_recipients}</span>
                                        <button className={styles.historyDetailBtn} onClick={() => { if (expandedCampaign === c.id) { setExpandedCampaign(null); } else { setExpandedCampaign(c.id); fetchCampaignDetails(c.id); } }}>
                                            <span className="material-symbols-outlined">{expandedCampaign === c.id ? "expand_less" : "expand_more"}</span>
                                            {expandedCampaign === c.id ? "Hide" : "Details"}
                                        </button>
                                        {c.status === "scheduled" && (
                                            <button className={styles.historyCancelBtn} onClick={() => cancelScheduledCampaign(c.id)}>🚫 Cancel</button>
                                        )}
                                        {c.failed > 0 && c.status === "sent" && (
                                            <button className={styles.historyRetryBtn} disabled={retrying} onClick={() => retryCampaign(c.id)}>
                                                {retrying ? "..." : "🔄 Retry All Failed"}
                                            </button>
                                        )}
                                    </div>
                                    {expandedCampaign === c.id && (
                                        <div className={styles.recipientList}>
                                            {loadingRecipients ? <div className={styles.loadingState}><div className={styles.spinnerSmall} /></div> : (
                                                campaignRecipients.length === 0 ? <p className={styles.emptyState} style={{ padding: 12 }}>No recipient data</p> : (
                                                    <>
                                                        <div className={styles.recipientListHeader}>
                                                            <span>Recipient</span><span>Status</span><span>Action</span>
                                                        </div>
                                                        {campaignRecipients.map(r => (
                                                            <div key={r.id} className={`${styles.recipientRow} ${r.status === "failed" ? styles.recipientRowFailed : r.status === "sent" ? styles.recipientRowSent : ""}`}>
                                                                <div className={styles.recipientRowInfo}>
                                                                    <strong>{r.firstname} {r.lastname}</strong>
                                                                    <span>{r.email}</span>
                                                                </div>
                                                                <div>
                                                                    <span className={`${styles.recipientStatusBadge} ${styles[`recipientStatus_${r.status}`] || ""}`}>
                                                                        {r.status === "sent" ? "✅ Sent" : r.status === "failed" ? "❌ Failed" : "⏳ Pending"}
                                                                    </span>
                                                                    {r.error_message && <span className={styles.recipientError} title={r.error_message}>{r.error_message.substring(0, 40)}</span>}
                                                                </div>
                                                                <div>
                                                                    {r.status === "failed" && (
                                                                        <button className={styles.retryBtn} disabled={retrying} onClick={() => retryCampaign(c.id, [r.id])}>
                                                                            {retrying ? "..." : "🔄 Retry"}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* SAVE TEMPLATE MODAL */}
            {showSaveTemplateModal && (
                <div className={styles.modalOverlay} onClick={() => setShowSaveTemplateModal(false)}>
                    <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
                        <h3>{editingTemplateId ? "Update Template" : "Save as Template"}</h3>
                        <div className={styles.modalField}>
                            <label>Template Name *</label>
                            <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g., Welcome Email v2" />
                        </div>
                        <div className={styles.modalField}>
                            <label>Description</label>
                            <input type="text" value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} placeholder="Optional description..." />
                        </div>
                        <div className={styles.modalActions}>
                            <button onClick={() => setShowSaveTemplateModal(false)} className={styles.backStepBtn}>Cancel</button>
                            <button onClick={saveTemplate} disabled={!templateName.trim() || savingTemplate} className={styles.generateBtn}>
                                {savingTemplate ? "Saving..." : editingTemplateId ? "Update" : "Save Template"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MAIN */}
            <main className={styles.main}>

                {/* STEP 1: AUDIENCE */}
                {step === 1 && (
                    <div className={styles.stepContent}>
                        <div className={styles.stepHeader}><h2>Select Your Audience</h2><p>Choose who receives this campaign</p></div>
                        <div className={styles.filtersBar}>
                            <div className={styles.filterGroup}><span className="material-symbols-outlined">search</span>
                                <input type="search" placeholder="Search..." value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} className={styles.filterInput} />
                            </div>
                            <select value={filters.role} onChange={e => setFilters(p => ({ ...p, role: e.target.value }))} className={styles.filterSelect}>
                                <option value="all">All Roles</option><option value="user">Users</option><option value="admin">Admins</option>
                            </select>
                            <select value={filters.active} onChange={e => setFilters(p => ({ ...p, active: e.target.value }))} className={styles.filterSelect}>
                                <option value="all">All Status</option><option value="true">Active</option><option value="false">Inactive</option>
                            </select>
                        </div>
                        <div className={styles.selectionBar}>
                            <button className={styles.selectAllBtn} onClick={selectAll}>{selectedIds.length === customers.length && customers.length > 0 ? "Deselect All" : "Select All"}</button>
                            <div className={styles.selectionInfo}><span className={styles.selectedBadge}>{selectedIds.length}</span><span>of {customers.length} selected</span></div>
                        </div>
                        <div className={styles.customerList}>
                            {loadingCustomers ? <div className={styles.loadingState}><div className={styles.spinner} /><p>Loading...</p></div>
                            : customers.length === 0 ? <div className={styles.emptyState}><span>🔍</span><p>No customers match</p></div>
                            : customers.map(c => (
                                <div key={c.id} className={`${styles.customerCard} ${selectedIds.includes(c.id) ? styles.customerCardSelected : ""}`} onClick={() => toggleSelect(c.id)}>
                                    <div className={styles.customerCheck}><div className={`${styles.checkbox} ${selectedIds.includes(c.id) ? styles.checkboxChecked : ""}`}>{selectedIds.includes(c.id) && "✓"}</div></div>
                                    <div className={styles.customerAvatar}>{(c.firstname?.[0] || "?").toUpperCase()}</div>
                                    <div className={styles.customerInfo}><strong>{c.firstname} {c.lastname}</strong><span>{c.email}</span></div>
                                    <div className={styles.customerMeta}><span className={`${styles.roleBadge} ${c.role === "admin" ? styles.roleBadgeAdmin : ""}`}>{c.role}</span></div>
                                </div>
                            ))}
                        </div>
                        <div className={styles.stepActions}><div />
                            <button className={styles.nextBtn} disabled={!selectedIds.length} onClick={() => setStep(2)}>Continue with {selectedIds.length} <span className="material-symbols-outlined">arrow_forward</span></button>
                        </div>
                    </div>
                )}

                {/* STEP 2: DESIGN */}
                {step === 2 && (
                    <div className={styles.stepContent}>
                        <div className={styles.stepHeader}><h2>Design Your Email</h2><p>Pick images, upload a logo, and let AI craft a modern email — or load a saved template</p></div>

                        {/* Templates Quick Access */}
                        {templates.length > 0 && (
                            <div className={styles.templateQuickAccess}>
                                <h4>📋 Your Templates</h4>
                                <div className={styles.templateChips}>
                                    {templates.slice(0, 6).map(t => (
                                        <button key={t.id} className={styles.templateChip} onClick={() => loadTemplate(t.id)}>
                                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>description</span> {t.name}
                                        </button>
                                    ))}
                                    {templates.length > 6 && <button className={styles.templateChip} onClick={() => setShowTemplates(true)}>View all ({templates.length})</button>}
                                </div>
                            </div>
                        )}

                        <div className={styles.brandRow}>
                            <div className={styles.brandCard}>
                                <h4>📎 Brand Logo</h4><p>Upload your logo for the email header</p>
                                <div className={styles.logoUploadArea}>
                                    {logoPreview ? (
                                        <div className={styles.logoPreviewBox}>
                                            <img src={logoPreview} alt="Logo" className={styles.logoPreviewImg} />
                                            <button className={styles.logoRemoveBtn} onClick={() => { setLogoUrl(""); setLogoPreview(""); }}>×</button>
                                            {uploadingLogo && <div className={styles.logoUploading}><div className={styles.spinnerSmall} /></div>}
                                        </div>
                                    ) : (
                                        <label className={styles.logoDropzone}><span className="material-symbols-outlined">cloud_upload</span><span>Upload logo</span><span className={styles.dropzoneHint}>PNG, JPG, SVG • Max 5MB</span><input type="file" accept="image/*" onChange={handleLogoUpload} hidden /></label>
                                    )}
                                </div>
                            </div>
                            <div className={styles.brandCard}>
                                <h4>🖼️ Email Images</h4><p>Pick photos or skip — AI auto-selects matching images</p>
                                <div className={styles.emailImagesArea}>
                                    {imageUrls.length === 0 ? (
                                        <div className={styles.noImages}><span className="material-symbols-outlined" style={{ fontSize: 28, color: "#1A56DB" }}>auto_awesome</span><span>AI auto-selects images</span></div>
                                    ) : (
                                        <div className={styles.imageChips}>{imageUrls.map((url, i) => <div key={i} className={styles.imageChip}><img src={url} alt="" /><button onClick={() => removeFromEmailImages(url)}>×</button></div>)}</div>
                                    )}
                                    <div className={styles.imageAddBtns}>
                                        <button className={styles.imageAddBtn} onClick={() => setShowImagePanel(true)}>🖼️ Browse</button>
                                        <label className={styles.imageAddBtn}>📤 Upload<input type="file" accept="image/*" hidden onChange={async (e) => {
                                            const f = e.target.files?.[0]; if (!f) return;
                                            try { const d = await uploadFile(f); const u = d.success ? d.url : URL.createObjectURL(f); setImageUrls(p => [...p, u]); } catch { setImageUrls(p => [...p, URL.createObjectURL(f)]); }
                                        }} /></label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {showImagePanel && step === 2 && (
                            <div className={styles.inlineUnsplash}>
                                <div className={styles.inlineUnsplashHeader}><h4>🖼️ Photo Collection</h4><button onClick={() => setShowImagePanel(false)} className={styles.closeBtn}>×</button></div>
                                <PhotoGrid selectedUrls={imageUrls} onSelect={(p) => imageUrls.includes(p.url) ? removeFromEmailImages(p.url) : addToEmailImages(p.url)} />
                            </div>
                        )}

                        <div className={styles.quickPrompts}><h3>Quick Templates</h3>
                            <div className={styles.quickPromptsGrid}>{quickPrompts.map((qp, i) => <button key={i} className={styles.quickPromptCard} onClick={() => setAiPrompt(qp.prompt)}><span className={styles.quickPromptLabel}>{qp.label}</span></button>)}</div>
                        </div>

                        <div className={styles.aiPromptSection}>
                            <div className={styles.aiPromptHeader}><span className={styles.aiIcon}>🤖</span><div><h3>Describe Your Email</h3><p>AI creates Expedia-quality emails with real photos.</p></div></div>
                            <textarea className={styles.aiPromptInput} rows={4} value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="e.g., Warm welcome email for new users..." />
                            <div className={styles.subjectInput}><label>Subject Line</label><input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g., Welcome to RoamRite!" /></div>
                            <div className={styles.personalizationHint}><span className="material-symbols-outlined">info</span><span>Uses <strong>{"{{firstName}}"}</strong> — auto-replaced per user.</span></div>
                            {generationError && <div className={styles.errorMsg}>{generationError}</div>}
                        </div>

                        <div className={styles.stepActions}>
                            <button className={styles.backStepBtn} onClick={() => setStep(1)}><span className="material-symbols-outlined">arrow_back</span> Back</button>
                            <button className={styles.generateBtn} disabled={!aiPrompt.trim() || generating} onClick={generateEmail}>
                                {generating ? <><div className={styles.spinnerSmall} />Designing...</> : <><span className={styles.sparkle}>✨</span>Generate</>}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3: EDIT & PREVIEW */}
                {step === 3 && (
                    <div className={styles.stepContent}>
                        <div className={styles.stepHeader}><h2>Edit & Preview</h2><p>Use the visual editor to tweak every element — or edit HTML directly</p></div>

                        <div className={styles.subjectEditBar}><label>Subject:</label><input type="text" value={subject} onChange={e => setSubject(e.target.value)} className={styles.subjectEditInput} placeholder="Subject line" /></div>

                        <div className={styles.editorToolbar}>
                            <div className={styles.editorToggle}>
                                <button className={`${styles.toggleBtn} ${editMode === "preview" ? styles.toggleBtnActive : ""}`} onClick={() => { setEditMode("preview"); setSelectedElement(null); }}><span className="material-symbols-outlined">visibility</span> Preview</button>
                                <button className={`${styles.toggleBtn} ${editMode === "visual" ? styles.toggleBtnActive : ""}`} onClick={() => setEditMode("visual")}><span className="material-symbols-outlined">edit</span> Visual</button>
                                <button className={`${styles.toggleBtn} ${editMode === "code" ? styles.toggleBtnActive : ""}`} onClick={() => { setEditMode("code"); setSelectedElement(null); }}><span className="material-symbols-outlined">code</span> HTML</button>
                            </div>
                            <div className={styles.toolbarActions}>
                                <button className={`${styles.toolbarBtn} ${showImagePanel ? styles.toolbarBtnActive : ""}`} onClick={() => { setShowImagePanel(!showImagePanel); if (!showImagePanel) setImagePanelTab("current"); }}>
                                    <span className="material-symbols-outlined">image</span> Images
                                </button>
                                <button className={styles.toolbarBtn} onClick={() => setShowFindReplace(!showFindReplace)}><span className="material-symbols-outlined">find_replace</span> Find</button>
                                <button className={styles.toolbarBtn} onClick={() => { setShowSaveTemplateModal(true); setEditingTemplateId(null); setTemplateName(""); setTemplateDesc(""); }}>
                                    <span className="material-symbols-outlined">save</span> Save Template
                                </button>
                                <button className={`${styles.toolbarBtn} ${showAiChat ? styles.toolbarBtnActive : ""}`} onClick={() => setShowAiChat(!showAiChat)}>
                                    <span className="material-symbols-outlined">smart_toy</span> AI Edit
                                </button>
                                <button className={styles.regenerateBtn} onClick={() => setStep(2)}><span className="material-symbols-outlined">refresh</span> Regenerate</button>
                            </div>
                        </div>

                        {showFindReplace && (
                            <div className={styles.findReplaceBar}>
                                <input placeholder="Find..." value={findText} onChange={e => setFindText(e.target.value)} />
                                <input placeholder="Replace..." value={replaceText} onChange={e => setReplaceText(e.target.value)} />
                                <button onClick={doFindReplace} disabled={!findText}>Replace All</button>
                                <button onClick={() => setShowFindReplace(false)} className={styles.closeBtnSmall}>×</button>
                            </div>
                        )}

                        <div className={styles.editorLayout}>
                            {/* VISUAL EDITOR MODE */}
                            {editMode === "visual" && (
                                <div className={styles.visualEditorPanel}>
                                    <div className={styles.visualEditorHeader}>
                                        <h4>📝 Email Elements</h4>
                                        <span className={styles.visualEditorHint}>Click an element to edit</span>
                                    </div>
                                    <div className={styles.visualElementList}>
                                        {parsedElements.map((el, i) => (
                                            <div key={`${el.type}-${el.index}`}
                                                className={`${styles.visualElement} ${selectedElement === i ? styles.visualElementActive : ""} ${styles[`visualElement_${el.type}`]}`}
                                                onClick={() => selectElementForEdit(i)}>
                                                <div className={styles.visualElementIcon}>
                                                    <span className="material-symbols-outlined">
                                                        {el.type === "heading" ? "title" : el.type === "paragraph" ? "notes" : el.type === "link" ? "link" : "image"}
                                                    </span>
                                                </div>
                                                <div className={styles.visualElementContent}>
                                                    <span className={styles.visualElementType}>{el.type === "heading" ? el.tag.toUpperCase() : el.type === "link" ? "Link" : el.type === "image" ? "Image" : "Text"}</span>
                                                    <span className={styles.visualElementPreview}>
                                                        {el.type === "image" ? (el.alt || el.src?.split("/").pop()?.substring(0, 30)) : (el.content || "").substring(0, 60)}{(el.content || "").length > 60 ? "..." : ""}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {parsedElements.length === 0 && <p className={styles.emptyState}>No editable elements found</p>}
                                    </div>

                                    {/* PROPERTY EDITOR */}
                                    {selEl && (
                                        <div className={styles.propertyEditor}>
                                            <div className={styles.propEditorHeader}>
                                                <h4>Properties — {selEl.type === "heading" ? selEl.tag.toUpperCase() : selEl.type}</h4>
                                                <button onClick={deleteElement} className={styles.propDeleteBtn} title="Delete element"><span className="material-symbols-outlined">delete</span></button>
                                            </div>

                                            {/* TEXT CONTENT */}
                                            {(selEl.type === "heading" || selEl.type === "paragraph" || selEl.type === "link") && (
                                                <div className={styles.propGroup}>
                                                    <label>Text Content</label>
                                                    <textarea value={elementEditText} onChange={e => updateElementText(e.target.value)} rows={selEl.type === "paragraph" ? 4 : 2} className={styles.propTextarea} />
                                                </div>
                                            )}

                                            {/* LINK HREF */}
                                            {selEl.type === "link" && (
                                                <div className={styles.propGroup}>
                                                    <label>Link URL (href)</label>
                                                    <input type="url" value={elementEditHref} onChange={e => updateElementHref(e.target.value)} className={styles.propInput} placeholder="https://..." />
                                                </div>
                                            )}

                                            {/* IMAGE ALT */}
                                            {selEl.type === "image" && (
                                                <div className={styles.propGroup}>
                                                    <label>Alt Text</label>
                                                    <input type="text" value={elementEditText} onChange={e => updateElementText(e.target.value)} className={styles.propInput} placeholder="Describe the image..." />
                                                </div>
                                            )}

                                            {/* FONT FAMILY */}
                                            {selEl.type !== "image" && (
                                                <div className={styles.propGroup}>
                                                    <label>Font Family</label>
                                                    <select value={selFontFamily} onChange={e => updateElementStyle("font-family", e.target.value)} className={styles.propSelect}>
                                                        <option value="">Default</option>
                                                        {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                                    </select>
                                                </div>
                                            )}

                                            {/* FONT SIZE */}
                                            {selEl.type !== "image" && (
                                                <div className={styles.propGroup}>
                                                    <label>Font Size</label>
                                                    <select value={selFontSize} onChange={e => updateElementStyle("font-size", e.target.value)} className={styles.propSelect}>
                                                        <option value="">Default</option>
                                                        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </div>
                                            )}

                                            {/* FONT WEIGHT */}
                                            {selEl.type !== "image" && (
                                                <div className={styles.propRow}>
                                                    <div className={styles.propGroup}>
                                                        <label>Weight</label>
                                                        <select value={selFontWeight} onChange={e => updateElementStyle("font-weight", e.target.value)} className={styles.propSelect}>
                                                            <option value="">Default</option>
                                                            <option value="400">Normal</option>
                                                            <option value="500">Medium</option>
                                                            <option value="600">Semi-Bold</option>
                                                            <option value="700">Bold</option>
                                                            <option value="800">Extra Bold</option>
                                                        </select>
                                                    </div>
                                                    <div className={styles.propGroup}>
                                                        <label>Align</label>
                                                        <div className={styles.propAlignBtns}>
                                                            {["left", "center", "right"].map(a => (
                                                                <button key={a} className={`${styles.propAlignBtn} ${selAlign === a ? styles.propAlignBtnActive : ""}`} onClick={() => updateElementStyle("text-align", a)}>
                                                                    <span className="material-symbols-outlined">format_align_{a}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* COLORS */}
                                            {selEl.type !== "image" && (
                                                <div className={styles.propRow}>
                                                    <div className={styles.propGroup}>
                                                        <label>Text Color</label>
                                                        <div className={styles.propColorPicker}>
                                                            <input type="color" value={selColor || "#000000"} onChange={e => updateElementStyle("color", e.target.value)} />
                                                            <input type="text" value={selColor} onChange={e => updateElementStyle("color", e.target.value)} placeholder="#000000" className={styles.propColorText} />
                                                        </div>
                                                    </div>
                                                    <div className={styles.propGroup}>
                                                        <label>Background</label>
                                                        <div className={styles.propColorPicker}>
                                                            <input type="color" value={selBgColor || "#ffffff"} onChange={e => updateElementStyle("background-color", e.target.value)} />
                                                            <input type="text" value={selBgColor} onChange={e => updateElementStyle("background-color", e.target.value)} placeholder="transparent" className={styles.propColorText} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* QUICK COLORS */}
                                            {selEl.type !== "image" && (
                                                <div className={styles.propGroup}>
                                                    <label>Quick Colors</label>
                                                    <div className={styles.propQuickColors}>
                                                        {["#0F1923", "#1A56DB", "#059669", "#4A5567", "#8C95A4", "#E53E3E", "#DD6B20", "#D69E2E", "#FFFFFF"].map(c => (
                                                            <button key={c} className={styles.propQuickColor} style={{ background: c, border: c === "#FFFFFF" ? "1px solid #ddd" : "none" }}
                                                                onClick={() => updateElementStyle("color", c)} />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* PREVIEW / CODE */}
                            <div className={styles.editorMain}>
                                {editMode === "code" ? (
                                    <textarea className={styles.codeEditor} value={emailHtml} onChange={e => setEmailHtml(e.target.value)} spellCheck={false} />
                                ) : (
                                    <div className={styles.previewContainer}>
                                        <div className={styles.previewFrame}>
                                            <div className={styles.previewHeader}><div className={styles.previewDots}><span /><span /><span /></div><div className={styles.previewSubject}>{subject || "No subject"}</div></div>
                                            <iframe ref={previewRef} srcDoc={personalizeHtml(emailHtml)} className={styles.previewIframe} title="Preview" sandbox="allow-same-origin allow-scripts" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* AI CHAT PANEL */}
                            {showAiChat && (
                                <div className={styles.aiChatPanel}>
                                    <div className={styles.aiChatHeader}>
                                        <div className={styles.aiChatHeaderLeft}>
                                            <span className="material-symbols-outlined">smart_toy</span>
                                            <div>
                                                <h4>AI Editor</h4>
                                                <span className={styles.aiChatSubtitle}>Ask me to change anything</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setShowAiChat(false)} className={styles.closeBtn}>×</button>
                                    </div>
                                    <div className={styles.aiChatMessages}>
                                        {aiChatMessages.length === 0 && (
                                            <div className={styles.aiChatEmpty}>
                                                <span className="material-symbols-outlined" style={{ fontSize: 36, color: "#1A56DB", marginBottom: 8 }}>auto_awesome</span>
                                                <p><strong>Ask me to edit your email!</strong></p>
                                                <p style={{ color: "#8C95A4", fontSize: 13 }}>I can change text, images, sections, layout, colors, add content, and more.</p>
                                                <div className={styles.aiChatSuggestions}>
                                                    {[
                                                        "Make the headline more exciting",
                                                        "Add a testimonial section",
                                                        "Change the CTA button to green",
                                                        "Add a pricing section with 3 tiers",
                                                        "Replace the hero image with a beach photo",
                                                        "Make the email longer with more travel tips",
                                                    ].map((s, i) => (
                                                        <button key={i} className={styles.aiChatSuggestionBtn} onClick={() => { setAiChatInput(s); }}>
                                                            {s}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {aiChatMessages.map((msg, i) => (
                                            <div key={i} className={`${styles.aiChatMsg} ${msg.role === "user" ? styles.aiChatMsgUser : styles.aiChatMsgAssistant}`}>
                                                <div className={styles.aiChatMsgBubble}>
                                                    {msg.role === "assistant" && <span className="material-symbols-outlined" style={{ fontSize: 14, marginRight: 4 }}>smart_toy</span>}
                                                    {msg.text}
                                                </div>
                                            </div>
                                        ))}
                                        {aiEditing && (
                                            <div className={`${styles.aiChatMsg} ${styles.aiChatMsgAssistant}`}>
                                                <div className={styles.aiChatMsgBubble}>
                                                    <div className={styles.aiChatTyping}>
                                                        <span /><span /><span />
                                                    </div>
                                                    Applying changes...
                                                </div>
                                            </div>
                                        )}
                                        <div ref={aiChatEndRef} />
                                    </div>
                                    <div className={styles.aiChatInputBar}>
                                        <input
                                            type="text"
                                            value={aiChatInput}
                                            onChange={e => setAiChatInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendAiEdit(); } }}
                                            placeholder="e.g., Make the headline bigger, add a pricing section..."
                                            className={styles.aiChatInput}
                                            disabled={aiEditing}
                                        />
                                        <button className={styles.aiChatSendBtn} onClick={sendAiEdit} disabled={!aiChatInput.trim() || aiEditing || !emailHtml}>
                                            {aiEditing ? <div className={styles.spinnerSmall} /> : <span className="material-symbols-outlined">send</span>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* IMAGE SIDE PANEL */}
                            {showImagePanel && (
                                <div className={styles.imagePanel}>
                                    <div className={styles.imagePanelHeader}>
                                        <h3>🖼️ {swappingImageIndex !== null ? "Pick Replacement" : "Images"}</h3>
                                        <button onClick={() => { setShowImagePanel(false); setSwappingImageIndex(null); }} className={styles.closeBtn}>×</button>
                                    </div>
                                    {swappingImageIndex !== null && (
                                        <div className={styles.swapBanner}><span className="material-symbols-outlined">swap_horiz</span><span>Replace #{swappingImageIndex + 1}</span><button onClick={() => setSwappingImageIndex(null)}>Cancel</button></div>
                                    )}
                                    <div className={styles.imagePanelTabs}>
                                        <button className={`${styles.panelTab} ${imagePanelTab === "current" ? styles.panelTabActive : ""}`} onClick={() => setImagePanelTab("current")}>In Email ({emailImages.length})</button>
                                        <button className={`${styles.panelTab} ${imagePanelTab === "photos" ? styles.panelTabActive : ""}`} onClick={() => setImagePanelTab("photos")}>Photos</button>
                                        <button className={`${styles.panelTab} ${imagePanelTab === "uploads" ? styles.panelTabActive : ""}`} onClick={() => setImagePanelTab("uploads")}>Upload</button>
                                    </div>
                                    {imagePanelTab === "current" && (
                                        <div className={styles.panelContent}>
                                            {emailImages.length === 0 ? <div className={styles.emptyState}><p>No images yet</p></div> : (
                                                <div className={styles.currentImagesList}>{emailImages.map((img, i) => (
                                                    <div key={i} className={styles.currentImageItem}>
                                                        <img src={img.src} alt={img.alt} className={styles.currentImageThumb} />
                                                        <div className={styles.currentImageInfo}><span className={styles.currentImageLabel}>#{i + 1}</span><span className={styles.currentImageAlt}>{img.alt || "No alt"}</span></div>
                                                        <div className={styles.currentImageActions}>
                                                            <button title="Swap" onClick={() => { setSwappingImageIndex(i); setImagePanelTab("photos"); }}><span className="material-symbols-outlined">swap_horiz</span></button>
                                                            <button title="Remove" onClick={() => removeImageFromHtml(i)}><span className="material-symbols-outlined">delete</span></button>
                                                        </div>
                                                    </div>
                                                ))}</div>
                                            )}
                                            <button className={styles.addImageBtnFull} onClick={() => setImagePanelTab("photos")}><span className="material-symbols-outlined">add_photo_alternate</span> Add Image</button>
                                        </div>
                                    )}
                                    {imagePanelTab === "photos" && <PhotoGrid selectedUrls={[]} onSelect={(p) => { if (swappingImageIndex !== null) replaceImageInHtml(swappingImageIndex, p.url); else insertImageUrl(p.url); }} />}
                                    {imagePanelTab === "uploads" && (
                                        <div className={styles.panelContent}>
                                            <label className={styles.uploadDropzone}><span className="material-symbols-outlined">cloud_upload</span><span>{uploadingImage ? "Uploading..." : "Upload"}</span><input type="file" accept="image/*" onChange={handleImageUpload} hidden disabled={uploadingImage} /></label>
                                            <div className={styles.panelGrid}>{uploadedImages.map((img, i) => (
                                                <div key={i} className={styles.panelPhoto} onClick={() => { if (swappingImageIndex !== null) replaceImageInHtml(swappingImageIndex, img.url); else insertImageUrl(img.url); }}>
                                                    <img src={img.thumb} alt={img.name} /><div className={styles.panelPhotoActions}><span className="material-symbols-outlined">{swappingImageIndex !== null ? "swap_horiz" : "add_photo_alternate"}</span></div>
                                                </div>
                                            ))}</div>
                                            {!uploadedImages.length && !uploadingImage && <p className={styles.emptyState}>No uploads</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={styles.stepActions}>
                            <button className={styles.backStepBtn} onClick={() => setStep(2)}><span className="material-symbols-outlined">arrow_back</span> Back</button>
                            <div className={styles.actionGroup}>
                                <button className={styles.testBtn} onClick={sendTestEmail} disabled={testSending || !subject}>{testSending ? "Sending..." : "📬 Send Test"}</button>
                                <button className={styles.nextBtn} disabled={!subject || !emailHtml} onClick={() => setStep(4)}>Review & Send <span className="material-symbols-outlined">arrow_forward</span></button>
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 4: SEND */}
                {step === 4 && (
                    <div className={styles.stepContent}>
                        <div className={styles.stepHeader}><h2>Ready to Launch 🚀</h2><p>Review, schedule, or send now</p></div>
                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryCard}><div className={styles.summaryIcon}>👥</div><div className={styles.summaryContent}><span className={styles.summaryNumber}>{selectedIds.length}</span><span className={styles.summaryLabel}>Recipients</span></div></div>
                            <div className={styles.summaryCard}><div className={styles.summaryIcon}>📧</div><div className={styles.summaryContent}><span className={styles.summarySubject}>{subject}</span><span className={styles.summaryLabel}>Subject</span></div></div>
                        </div>
                        <div className={styles.miniPreview}><h3>Preview</h3><div className={styles.miniPreviewFrame}><iframe srcDoc={personalizeHtml(emailHtml)} className={styles.miniPreviewIframe} title="Preview" sandbox="allow-same-origin allow-scripts" /></div></div>
                        <div className={styles.recipientPreview}>
                            <h3>Sending to {selectedIds.length}:</h3>
                            <div className={styles.recipientChips}>{customers.filter(c => selectedIds.includes(c.id)).slice(0, 20).map(c => <span key={c.id} className={styles.recipientChip}>{c.firstname} ({c.email})</span>)}{selectedIds.length > 20 && <span className={styles.recipientChip}>+{selectedIds.length - 20} more</span>}</div>
                        </div>
                        <div className={styles.personalizationHint}><span className="material-symbols-outlined">check_circle</span><span>Each email uses the customer&apos;s <strong>real first name</strong>.</span></div>

                        {/* Schedule Option */}
                        {!sendResult?.success && (
                            <div className={styles.scheduleSection}>
                                <h3>⏰ Schedule (optional)</h3>
                                <p>Leave empty to send immediately, or pick a future date/time.</p>
                                <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                                    className={styles.scheduleInput}
                                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} />
                                {scheduledAt && new Date(scheduledAt) > new Date() && (
                                    <div className={styles.schedulePreview}>
                                        <span className="material-symbols-outlined">schedule</span>
                                        <span>Scheduled for <strong>{new Date(scheduledAt).toLocaleString()}</strong></span>
                                        <button onClick={() => setScheduledAt("")} className={styles.scheduleClearBtn}>Clear</button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Save to Template */}
                        {!sendResult?.success && (
                            <div className={styles.saveTemplateBar}>
                                <button className={styles.toolbarBtn} onClick={() => { setShowSaveTemplateModal(true); setEditingTemplateId(null); setTemplateName(""); setTemplateDesc(""); }}>
                                    <span className="material-symbols-outlined">bookmark_add</span> Save as Template
                                </button>
                            </div>
                        )}

                        {sendResult && (
                            <div className={`${styles.sendResult} ${sendResult.success ? styles.sendResultSuccess : styles.sendResultError}`}>
                                {sendResult.success ? (
                                    sendResult.scheduled ? (
                                        <><div className={styles.sendResultIcon}>⏰</div><h3>Scheduled!</h3><p>Campaign scheduled for <strong>{new Date(sendResult.scheduledAt).toLocaleString()}</strong> · {sendResult.total} recipients</p></>
                                    ) : (
                                        <>
                                            <div className={styles.sendResultIcon}>🎉</div><h3>Sent!</h3>
                                            <p>✅ {sendResult.sent} delivered{sendResult.failed > 0 && ` · ❌ ${sendResult.failed} failed`}</p>
                                            {sendResult.failed > 0 && sendResult.campaignId && (
                                                <button className={styles.retryAllBtn} disabled={retrying} onClick={() => retryCampaign(sendResult.campaignId)}>
                                                    {retrying ? "Retrying..." : "🔄 Retry Failed Recipients"}
                                                </button>
                                            )}
                                            {sendResult.errors?.length > 0 && (
                                                <div className={styles.failedList}>
                                                    <h4>Failed recipients:</h4>
                                                    {sendResult.errors.map((e, i) => (
                                                        <div key={i} className={styles.failedItem}>
                                                            <span>{e.email}</span>
                                                            <span className={styles.failedError}>{e.error}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )
                                ) : <><div className={styles.sendResultIcon}>⚠️</div><h3>Failed</h3><p>{sendResult.error}</p></>}
                            </div>
                        )}
                        <div className={styles.stepActions}>
                            <button className={styles.backStepBtn} onClick={() => setStep(3)}><span className="material-symbols-outlined">arrow_back</span> Back</button>
                            {!sendResult?.success ? (
                                <button className={styles.sendBtn} disabled={sending} onClick={sendCampaign}>
                                    {sending ? <><div className={styles.spinnerSmall} /> Sending...</> : scheduledAt && new Date(scheduledAt) > new Date() ? <>⏰ Schedule Campaign</> : <>🚀 Send Campaign</>}
                                </button>
                            ) : (
                                <button className={styles.newCampaignBtn} onClick={() => { setStep(1); setSelectedIds([]); setAiPrompt(""); setEmailHtml(""); setSubject(""); setSendResult(null); setImageUrls([]); setScheduledAt(""); }}>✨ New Campaign</button>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};
